import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector, batch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { putCookie, useEffectOnce, useQuery, useViewport } from '@chilecompra/react-kit'
import { inIframe } from '../../modules/utils/inIframe'
import { Pagination, Skeleton, Spinner, Toast, Typography } from '@chilecompra/react-kit/components'
import SkeletonRow from './QuoteSearchResults.skeleton'
import Row from './QuoteSearchResults.row'
import routes from '../../config/settings/routes'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { topPage } from '../../modules/utils/topPage'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { onGetDownloadQuoteSearchResultsThunk } from './QuoteSearchResults.actions'
import {
  onCloseDialogWarningHabilityParticipate,
  onApplyErrorSupplierPrioritized,
  onGetQuoteSearchThunk,
  onOpenDialogWarningHabilityParticipate
} from '../QuoteSearchHeadingFilter/QuoteSearchHeadingFilters.actions'
import { onClearAttachedFilesSuccess, onDisabledInput } from '../QuoteBidSeller/QuoteBidSeller.actions'
import {
  onGetQuotePurchaseOrderChangeStatusThunk,
  onOpenPurchaseOrderChangeStatusDialog
} from '../PurchaseOrderChangeStatusDialog/PurchaseOrderChangeStatusDialog.actions'
import {
  AddAlertIcon,
  Card,
  IconArrowDownward,
  Link,
  Grid,
  NotFoundImg,
  NotificationNumber,
  NotificationSpan,
  NotificationWrapper,
  ResultsWrapper,
  Wrapper
} from './QuoteSearchResults.styles'
import { PROVIDER_PRIORIZED_IDENTIFIER_KEYS_DICTIONARY } from '../../config/settings/constants'

/*
 * The QuoteSearchResults' container.
 */
const QuoteSearchResults = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const { size } = useViewport()
  const { isBuyer } = useAuthorization()
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const { open } = useSelector(state => state.purchaseOrderChangeStatusDialog)
  const {
    download: { error: downloadError, loading: downloading },
    quotePurcharseOrderChangeStatus
  } = useSelector(state => state.quoteSearchResults)
  const {
    error,
    loading,
    openWarningHability,
    searchResult: { page, pageCount, resultCount, results, prioritized },
    supplierHability,
    supplierPrioritized
  } = useSelector(state => state.quoteSearchHeadingFilters)

  const isInitial = !loading && error === undefined && resultCount === undefined
  const hasNoResults = !loading && error === undefined && resultCount === 0
  const hasResults = !loading && error === undefined && typeof resultCount === 'number' && resultCount > 0

  const handleChangePage = (_, value) => {
    query.set('page', value)

    putCookie('search', query.toString())
    navigate({ pathname: routes.search, search: query.toString() }, { replace: true })

    dispatch(onGetQuoteSearchThunk({ query }))
  }

  const handleDownload = () => dispatch(onGetDownloadQuoteSearchResultsThunk({ query }))
  const handleClearAttachedFiles = () =>
    batch(() => {
      dispatch(onClearAttachedFilesSuccess())
    })
  const handleOpenDialogHabilityWarning = () => dispatch(onOpenDialogWarningHabilityParticipate())
  const handleCloseDialogHabilityWarning = () => {
    batch(() => {
      dispatch(onCloseDialogWarningHabilityParticipate())
    })
  }
  const handleClickDialog = () => {
    batch(() => {
      dispatch(onCloseDialogWarningHabilityParticipate())
      dispatch(onClearAttachedFilesSuccess())
    })
  }
  const handleClickPrioritized = () => {
    if (prioritized === PROVIDER_PRIORIZED_IDENTIFIER_KEYS_DICTIONARY.priorized) {
      batch(() => {
        dispatch(onDisabledInput())
        dispatch(onApplyErrorSupplierPrioritized())
      })
    }
  }

  const disabledInput = () => dispatch(onDisabledInput())
  const renderRowSkeleton = new Array(15).fill().map((_, index) => (
    <Card color="blank" elevation="1" key={index} margin="0 0 12px 0" padding="16px 16px">
      <SkeletonRow />
    </Card>
  ))
  const renderCurrenPage = results.map((row, key) => (
    <Card color="blank" elevation="1" enableHover key={key} margin="0 0 12px 0" padding="16px 16px">
      <Row
        activeResults={supplierHability}
        authInfo={authInfo}
        closeDialogHabilityWarning={handleCloseDialogHabilityWarning}
        data={row}
        disabledInput={disabledInput}
        handleClickDialog={handleClickDialog}
        handleClickPrioritized={handleClickPrioritized}
        loading={loading}
        onClearAttachedFiles={handleClearAttachedFiles}
        open={openWarningHability}
        openDialogHabilityWarning={handleOpenDialogHabilityWarning}
        supplierPrioritized={supplierPrioritized}
      />
    </Card>
  ))
  const renderPaginatorSkeleton = <Skeleton width="150px" />
  const isInIframe = useMemo(() => inIframe(), [])

  const handleOpenAlert = () => dispatch(onOpenPurchaseOrderChangeStatusDialog())

  useEffect(() => {
    topPage(isInIframe)
  }, [loading, isInIframe])

  useEffect(() => {
    topPage(isInIframe, open)
  }, [open, isInIframe])

  useEffectOnce(() => {
    if (!isBuyer) return
    dispatch(onGetQuotePurchaseOrderChangeStatusThunk())
  })

  return (
    <ResultsWrapper>
      <Grid container>
        {!downloading && downloadError && (
          <Grid container margin="32px 0">
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para realizar la descarga de información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </Grid>
        )}

        {!loading && error !== undefined && (
          <Grid container margin="16px 0 0">
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </Grid>
        )}

        <Grid item xs={12} textAlign="right">
          <Wrapper>
            {quotePurcharseOrderChangeStatus?.length > 0 && (
              <NotificationWrapper onClick={handleOpenAlert} margin={hasResults ? '0 32px 0 0' : '0'}>
                <AddAlertIcon />
                <NotificationNumber>
                  <NotificationSpan>{quotePurcharseOrderChangeStatus?.length}</NotificationSpan>
                </NotificationNumber>
              </NotificationWrapper>
            )}

            <ProtectByFeatures rule={features => features['download.link.excel.result']}>
              {loading && <Skeleton width="150px" />}
              {hasResults && (
                <>
                  <Link onClick={handleDownload}>
                    {downloading ? 'Descargando resultados...' : 'Descargar resultados en excel'}
                  </Link>
                  {downloading ? <Spinner size={15} /> : <IconArrowDownward margin="auto 0" />}
                </>
              )}
            </ProtectByFeatures>
          </Wrapper>
        </Grid>

        {isInitial && (
          <Grid item xs={12} textAlign="center">
            <Card color="blank" padding="75.5px 0 74.5px">
              <NotFoundImg alt="No se encontraron resultados" src="/assets/images/nofound.svg" />
              <Typography variant="h4" fontWeight="bold" margin="8px 0">
                Encuentra oportunidades de negocio en Compra Ágil
              </Typography>
              <Typography variant="body1" color="black" fontSize="14px" lineHeight="18px">
                Ingresa un criterio de búsqueda y haz click en <strong>Buscar</strong> para revisar oportunidades
                disponibles.
              </Typography>
            </Card>
          </Grid>
        )}

        {hasNoResults && (
          <Grid item xs={12} textAlign="center">
            <Card color="blank" padding="75.5px 0 74.5px">
              <NotFoundImg alt="No se encontraron resultados" src="/assets/images/nofound.svg" />
              <Typography variant="h4" fontWeight="bold" margin="8px 0">
                No se encontraron resultados para tu búsqueda
              </Typography>
              <Typography variant="body1" color="black3" fontSize="14px" lineHeight="18px">
                Intenta con otra palabra clave u otro criterio de búsqueda.
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={12} padding="15px 0 0">
          {loading && renderRowSkeleton}
          {!loading && renderCurrenPage}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} padding="15px 0 48px 0" textAlign="right">
          {loading && renderPaginatorSkeleton}
          {!loading && pageCount > 1 && (
            <Pagination
              boundaryCount={2}
              count={pageCount}
              page={page}
              size={size.isDownToTiny ? 'small' : 'medium'}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>
    </ResultsWrapper>
  )
}

export default QuoteSearchResults
